import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeading from '../components/PageHeading';
import { IconLock, IconMessageSquare } from '../components/Icons';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { trackEvent } from '../utils/analytics';
import { usePropFirmPresets, findPlan, money, formatVerified, rememberPreset, rememberedPreset } from '../utils/propFirmPresets';
import { askRiskloAi } from '../components/askRisklo/AskRiskloLauncher';
import PlannerRowDetail, { warningText } from '../components/propFirms/PlannerRowDetail';
import Explain from '../components/propFirms/Explain';
import MissingFirms from '../components/propFirms/MissingFirms';
import './PropFirmPlannerPage.css';

/*
 * Prop Firm Planner, in three steps:
 *  1. Choose your account: firm and plan, size, time to pass. The firm's
 *     verified rules show as a one-line summary; "Customize rules" opens a
 *     side panel (basics, then advanced).
 *  2. Your best bet: the strategy and size that passed most often, in plain words.
 *  3. Compare strategies: chance to pass, risk of failing, how long it usually took.
 * The server replays every brand futures strategy at every size against the
 * evaluation from many start dates; clicking a row shows how it went.
 */

const WINDOWS = [
  { days: 20, label: '1 month', sub: '20 trading days' },
  { days: 40, label: '2 months', sub: '40 trading days' },
  { days: 60, label: '3 months', sub: '60 trading days' },
  { days: 120, label: '6 months', sub: '120 trading days' },
];

const TRAILING = [
  { id: 'eod', label: 'Trails your highest close (end of day)', short: 'trails end of day' },
  { id: 'intraday', label: 'Trails during the day (intraday)', short: 'trails during the day' },
  { id: 'static', label: 'Never moves (static)', short: 'fixed' },
];

const INTRO_KEY = 'risklo.propFirmPlanner.introSeen';

/** The editable rules for one plan and size, from the firm's verified rules. */
export function rulesFor(plan, size) {
  if (!plan || !size) return null;
  const ev = plan.evaluation || {};
  const trailing = ev.trailing || (/intraday/i.test(plan.drawdownType || '') ? 'intraday' : 'eod');
  return {
    accountSize: size.accountSize,
    profitTarget: size.profitTarget ?? '',
    drawdown: size.drawdown ?? '',
    trailing,
    lockOffset: ev.lockOffset || 0,
    dailyLossLimit: size.dailyLossLimit ?? '',
    dailyLoss: ev.dailyLoss || 'pause',
    // A day that closed past the limit: its full loss counts (cautious), or only the limit.
    dailyLossCount: 'full',
    consistency: ev.consistency ?? '',
    minDays: ev.minDays ?? '',
    maxContracts: size.maxContracts ?? '',
  };
}

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(v >= 10 || v === 0 ? 0 : 1)}%`);

/** Failing 25% of the time or more is high risk; under 10% low. */
export function riskLevel(breachRate) {
  if (breachRate == null) return null;
  if (breachRate < 10) return { id: 'low', label: 'Low' };
  if (breachRate < 25) return { id: 'medium', label: 'Medium' };
  return { id: 'high', label: 'High' };
}
const passTone = (rate) => (rate >= 70 ? 'good' : rate >= 40 ? 'ok' : 'low');

/** "~5 weeks" for 24 trading days; short spans in days. */
export function timeToPass(days) {
  if (days == null) return null;
  if (days <= 7) return `${days} trading day${days === 1 ? '' : 's'}`;
  const weeks = Math.round(days / 5);
  return `~${weeks} week${weeks === 1 ? '' : 's'}`;
}

/** The rules as plain chips: "Target $3,000", "Max loss $2,500 · trails during the day", … */
export function rulesSummary(rules) {
  if (!rules) return [];
  const trail = TRAILING.find((t) => t.id === rules.trailing)?.short || '';
  return [
    rules.profitTarget ? `Target ${money(rules.profitTarget)}` : null,
    rules.drawdown ? `Max loss ${money(rules.drawdown)} · ${trail}` : null,
    rules.dailyLossLimit ? `Daily limit ${money(rules.dailyLossLimit)}` : 'No daily limit',
    rules.consistency ? `Best day ≤ ${rules.consistency}% of profit` : null,
    rules.minDays ? `${rules.minDays}+ trading days` : null,
    rules.maxContracts ? `Up to ${rules.maxContracts} NQ` : null,
  ].filter(Boolean);
}

function NumberField({ label, value, onChange, hint, prefix = '', min = 0 }) {
  return (
    <label className="pfp-field">
      <span>{label}</span>
      <div className="pfp-input">
        {prefix ? <i>{prefix}</i> : null}
        <input
          type="number"
          inputMode="decimal"
          min={min}
          value={value}
          placeholder="None"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function Chips({ label, options, value, onChange }) {
  return (
    <div className="pfp-chips-field" role="group" aria-label={label}>
      <span className="pfp-chips-label">{label}</span>
      <div className="pfp-chips">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`pfp-chip${o.value === value ? ' is-on' : ''}`}
            aria-pressed={o.value === value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const StepTitle = ({ n, title, children }) => (
  <div className="pfp-step-head">
    <h2><span className="pfp-step-n" aria-hidden="true">{n}</span>{title}</h2>
    {children}
  </div>
);

/** First visit: what the planner does, in three steps. */
function HowItWorks({ onClose }) {
  const steps = [
    ['Pick your account', 'Choose your prop firm, plan and account size. Its rules fill in for you.'],
    ['We replay every strategy', 'Each futures strategy is replayed at every size, starting on hundreds of different dates.'],
    ['See the odds', 'How often it passed, failed or ran out of time, and how long passing usually took.'],
  ];
  return (
    <section className="pfp-intro" aria-label="How the planner works">
      <ol>
        {steps.map(([t, s], i) => (
          <li key={t}><span className="pfp-step-n" aria-hidden="true">{i + 1}</span><div><b>{t}</b><small>{s}</small></div></li>
        ))}
      </ol>
      <button type="button" className="pfp-btn pfp-btn--ghost" onClick={onClose}>Got it</button>
    </section>
  );
}

/** A ring showing the pass rate. */
function PassRing({ rate }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, Number(rate) || 0));
  return (
    <svg className={`pfp-ring is-${passTone(v)}`} viewBox="0 0 84 84" role="img" aria-label={`Passed ${pct(rate)} of the time`}>
      <circle className="pfp-ring-track" cx="42" cy="42" r={r} />
      <circle className="pfp-ring-fill" cx="42" cy="42" r={r} strokeDasharray={`${(c * v) / 100} ${c}`} transform="rotate(-90 42 42)" />
      <text x="42" y="40" textAnchor="middle">{Math.round(v)}%</text>
      <text x="42" y="54" textAnchor="middle" className="pfp-ring-sub">passed</text>
    </svg>
  );
}

const RiskBadge = ({ rate }) => {
  const risk = riskLevel(rate);
  if (!risk) return null;
  return <span className={`pfp-risk is-${risk.id}`}>{risk.label} <small>{pct(rate)}</small></span>;
};

/** What each column means, behind the ⓘ in its header. */
const COLUMN_HELP = {
  'Chance to pass': 'Out of every start date replayed, the share where profit reached the target within the rules and the time allowed, before the balance hit the max loss. Open a strategy and tap the numbers for the exact counts.',
  'Risk of failing': 'The share of tries that hit the max loss (or, on plans that fail you for it, the daily loss limit). Low is under 10%, medium under 25%, high 25% or more.',
  'Usually passes in': 'The median time a passing try took, counted from its start date, including days with no trade. A week is 5 trading days.',
};
const HelpLabel = ({ label }) => <>{label} <Explain title={label} body={<p>{COLUMN_HELP[label]}</p>} icon /></>;

const rowKey = (r) => `${r.strategy}|${r.size}`;

function ResultsList({ rows, openKey, onToggle, renderDetail, rules }) {
  return (
    <div className="pfp-table-wrap">
      <table className="pfp-table">
        <thead>
          <tr>
            <th className="num pfp-col-rank">#</th>
            <th>Strategy</th>
            <th><HelpLabel label="Chance to pass" /></th>
            <th><HelpLabel label="Risk of failing" /></th>
            <th className="num"><HelpLabel label="Usually passes in" /></th>
            <th aria-label="Details" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const open = openKey === rowKey(r);
            return (
              <React.Fragment key={rowKey(r)}>
                <tr
                  className={`pfp-row${i === 0 ? ' is-top' : ''}${open ? ' is-open' : ''}${r.warning ? ' is-flagged' : ''}`}
                  onClick={() => onToggle(r)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(r); } }}
                  tabIndex={0}
                  aria-expanded={open}
                  title="See how this evaluation went"
                >
                  <td className="num pfp-col-rank">{i + 1}</td>
                  <td className="pfp-col-name">
                    <div className="pfp-strategy-cell">
                      <span className="pfp-strategy">{r.strategy}</span>
                      <span className="pfp-size">{r.size}</span>
                      {r.warning ? <span className="pfp-flag" title={warningText(r.warning, rules)}>Too risky for this account</span> : null}
                    </div>
                  </td>
                  <td className="pfp-col-pass">
                    <span className={`pfp-pass is-${passTone(r.passRate)}`}>
                      <span className="pfp-bar" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, r.passRate))}%` }} /></span>
                      <b>{pct(r.passRate)}</b>
                    </span>
                  </td>
                  <td className="pfp-col-risk"><RiskBadge rate={r.breachRate} /></td>
                  <td className="num pfp-col-time">
                    {r.medianDaysToPass == null ? <span className="pfp-muted">Didn’t pass</span> : (
                      <>{timeToPass(r.medianDaysToPass)} <small>{r.medianDaysToPass} days</small></>
                    )}
                  </td>
                  <td className="pfp-chev" aria-hidden="true">{open ? '▾' : '▸'}</td>
                </tr>
                {open ? (
                  <tr className="pfp-detail-row">
                    <td colSpan={6}>{renderDetail(r)}</td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Customize rules: a side panel editing a copy, applied with "Apply and run". */
function RulesPanel({ open, rules, firmRules, onApply, onClose }) {
  const [draft, setDraft] = useState(rules);
  useEffect(() => { if (open) setDraft(rules); }, [open, rules]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open || !draft) return null;
  const set = (key) => (value) => setDraft((r) => ({ ...r, [key]: value }));
  const differs = firmRules && Object.keys(firmRules).some((k) => String(firmRules[k] ?? '') !== String(draft[k] ?? ''));
  return (
    <div className="pfp-drawer-root" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="pfp-drawer" role="dialog" aria-modal="true" aria-label="Customize rules">
        <div className="pfp-drawer-head">
          <div>
            <h2>Customize rules</h2>
            <p>These start as your firm’s rules. Change any of them to see how the odds move.</p>
          </div>
          <button type="button" className="pfx-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="pfp-drawer-body">
          <h3>The basics</h3>
          <div className="pfp-rules-grid">
            <NumberField label="Profit target" prefix="$" value={draft.profitTarget} onChange={set('profitTarget')} hint="What you need to make to pass." />
            <NumberField label="Most you can lose (max drawdown)" prefix="$" value={draft.drawdown} onChange={set('drawdown')} hint="Hit this and the evaluation fails." />
            <NumberField label="Daily loss limit" prefix="$" value={draft.dailyLossLimit} onChange={set('dailyLossLimit')} hint="Blank if your firm has none." />
          </div>
          <details className="pfp-advanced">
            <summary>More rules</summary>
            <div className="pfp-rules-grid">
              <label className="pfp-field">
                <span>How the max loss moves</span>
                <select value={draft.trailing} onChange={(e) => set('trailing')(e.target.value)}>
                  {TRAILING.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </label>
              <NumberField label="Max loss stops moving at start +" prefix="$" value={draft.lockOffset} onChange={set('lockOffset')} hint="Apex: $100. Most firms: $0." />
              <label className="pfp-field">
                <span>Hitting the daily loss limit</span>
                <select value={draft.dailyLoss} onChange={(e) => set('dailyLoss')(e.target.value)} disabled={!draft.dailyLossLimit}>
                  <option value="pause">Stops trading for the day</option>
                  <option value="fail">Fails the evaluation</option>
                </select>
              </label>
              <label className="pfp-field">
                <span>A day that closed past the limit counts</span>
                <select
                  value={draft.dailyLossCount || 'full'}
                  onChange={(e) => set('dailyLossCount')(e.target.value)}
                  disabled={!draft.dailyLossLimit || draft.dailyLoss === 'fail'}
                >
                  <option value="full">Its full loss (cautious)</option>
                  <option value="capped">Only the limit (the firm stops you)</option>
                </select>
                <small>Daily results can’t show the path during the day, so neither is exact. Each strategy shows both.</small>
              </label>
              <NumberField label="Consistency (best day ≤ % of profit)" value={draft.consistency} onChange={set('consistency')} hint="Blank if none." />
              <NumberField label="Minimum trading days" value={draft.minDays} onChange={set('minDays')} hint="Blank if none." />
              <NumberField label="Most contracts (in NQ)" value={draft.maxContracts} onChange={set('maxContracts')} hint="10 MNQ = 1 NQ. Bigger sizes aren’t tried." />
            </div>
          </details>
        </div>
        <div className="pfp-drawer-foot">
          {differs ? <button type="button" className="pfp-btn pfp-btn--ghost" onClick={() => setDraft(firmRules)}>Use the firm’s rules</button> : <span />}
          <div>
            <button type="button" className="pfp-btn pfp-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="pfp-btn" onClick={() => onApply(draft)}>Apply and run</button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Skeleton({ hero = false }) {
  return (
    <div className="pfp-skeleton" aria-label="Replaying every strategy…">
      {hero ? <div className="pfp-skel pfp-skel-hero" /> : [0, 1, 2, 3].map((i) => <div key={i} className="pfp-skel pfp-skel-row" />)}
    </div>
  );
}

export default function PropFirmPlannerPage({ user, isPro = false, onUpgrade }) {
  const { firms, live, notListed } = usePropFirmPresets();
  const plans = useMemo(() => firms.flatMap((f) => f.plans.map((p) => ({ firm: f, plan: p }))), [firms]);
  const [planId, setPlanId] = useState(() => rememberedPreset() || 'topstep-combine');
  const picked = findPlan(firms, planId) || plans[0] || null;
  const firm = picked?.firm || null;
  const plan = picked?.plan || null;
  const [sizeLabel, setSizeLabel] = useState('50K');
  const size = plan?.sizes.find((s) => s.label === sizeLabel) || plan?.sizes[0] || null;
  const [rules, setRules] = useState(() => rulesFor(plan, size));
  const [windowDays, setWindowDays] = useState(60);
  const [view, setView] = useState('best');
  const [openKey, setOpenKey] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [introSeen, setIntroSeen] = useState(() => { try { return localStorage.getItem(INTRO_KEY) === '1'; } catch (_e) { return true; } });
  const runId = useRef(0);
  const listRef = useRef(null);

  // A new plan or size starts from the firm's verified rules.
  const planKey = `${plan?.id}|${size?.label}|${live ? 'live' : 'fallback'}`;
  useEffect(() => { setRules(rulesFor(plan, size)); }, [planKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useCallback(async (useRules = rules) => {
    if (!useRules || !user?.email) return;
    const id = runId.current + 1;
    runId.current = id;
    setRunning(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.propFirmPlannerRank, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ rules: useRules, windowDays }),
      });
      const data = await res.json().catch(() => null);
      if (id !== runId.current) return;
      if (!res.ok || !data?.success) {
        // No JSON at all: the server doesn't have the planner (not updated yet) or a proxy timed out.
        if (!data) {
          throw new Error(res.status === 404
            ? 'The server doesn’t have the Prop Firm Planner yet. It may still be updating; try again in a few minutes.'
            : `The server didn’t answer properly (error ${res.status}). Try again shortly.`);
        }
        throw new Error(data.error || `Couldn’t run the planner (error ${res.status}).`);
      }
      setResult(data);
      setOpenKey(null);
      trackEvent('prop_firm_planner_run', { plan: plan?.id, size: size?.label, window: windowDays, strategies: data.strategyCount });
    } catch (err) {
      if (id === runId.current) setError(err.message);
    } finally {
      if (id === runId.current) setRunning(false);
    }
  }, [rules, windowDays, user?.email, plan?.id, size?.label]);

  // Run when a plan or size is picked (with the firm's rules), and when the
  // time allowed changes (keeping any edited rules).
  const lastPlanKey = useRef(null);
  useEffect(() => {
    const newPlan = lastPlanKey.current !== planKey;
    lastPlanKey.current = planKey;
    const use = newPlan ? rulesFor(plan, size) : rules;
    if (use) run(use);
  }, [planKey, windowDays, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const choosePlan = (id) => {
    setPlanId(id);
    rememberPreset(id);
    const next = findPlan(firms, id)?.plan;
    if (next && !next.sizes.some((s) => s.label === sizeLabel)) setSizeLabel(next.sizes[0]?.label || '');
  };

  const firmRules = useMemo(() => rulesFor(plan, size), [plan, size]);
  const edited = useMemo(() => (
    firmRules && rules ? Object.keys(firmRules).some((k) => String(firmRules[k] ?? '') !== String(rules[k] ?? '')) : false
  ), [firmRules, rules]);
  const applyRules = (next) => { setRules(next); setRulesOpen(false); run(next); };

  const rows = result ? (view === 'best' ? result.bestPerStrategy : result.rows) : [];
  const locked = result ? (view === 'best' ? result.lockedBest : result.lockedRows) : 0;
  const top = result?.bestPerStrategy?.[0] || null;
  const planLabel = firm && plan && size ? `${firm.firm} · ${plan.plan} · ${size.label}` : '';

  const askAi = () => {
    if (!top) return;
    askRiskloAi(
      `The Prop Firm Planner ranked ${top.strategy} at ${top.size} first for the ${firm.firm} ${plan.plan} ${size.label} `
      + `(passed ${pct(top.passRate)} of starts, blew up ${pct(top.breachRate)}, median ${top.medianDaysToPass ?? '—'} days to pass). `
      + 'What should I know before choosing a strategy and size for this account?',
    );
  };
  const askAboutRow = (r) => askRiskloAi(
    `In the Prop Firm Planner, ${r.strategy} at ${r.size} on the ${firm.firm} ${plan.plan} ${size.label} passed ${pct(r.passRate)} `
    + `of ${r.runs} start days, blew up ${pct(r.breachRate)} and ran out of time ${pct(r.timeoutRate)}. `
    + 'Explain what that means for running it on this account, and what to watch out for.',
  );
  // "See how it went": open the best bet's row in the list below.
  const seeTop = () => {
    if (!top) return;
    setView('best');
    setOpenKey(rowKey(top));
    setTimeout(() => listRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 50);
  };

  if (!user?.email) {
    return (
      <div className="pfp-page">
        <PageHeading title="Prop Firm Planner" description="Sign in to see which strategy and size passed your prop firm’s evaluation most often." />
      </div>
    );
  }

  const windowLabel = WINDOWS.find((w) => w.days === (result?.windowDays || windowDays))?.label || `${windowDays} trading days`;

  return (
    <div className="pfp-page">
      <PageHeading
        title="Prop Firm Planner"
        description="See which of your futures strategies, at which size, has the best odds of passing your prop firm’s evaluation."
      />

      {!introSeen ? (
        <HowItWorks onClose={() => { setIntroSeen(true); try { localStorage.setItem(INTRO_KEY, '1'); } catch (_e) { /* per device */ } }} />
      ) : null}

      <section className="pfp-card pfp-step" aria-label="Choose your account">
        <StepTitle n={1} title="Choose your account" />
        <div className="pfp-pick">
          <label className="pfp-field pfp-field--firm">
            <span>Prop firm and plan</span>
            <select value={plan?.id || ''} onChange={(e) => choosePlan(e.target.value)}>
              {firms.map((f) => (
                <optgroup key={f.key} label={f.firm}>
                  {f.plans.map((p) => <option key={p.id} value={p.id}>{f.firm} · {p.plan}{f.confirmed === false ? ' (rules not yet confirmed)' : ''}</option>)}
                </optgroup>
              ))}
            </select>
            <MissingFirms firms={notListed} />
          </label>
          <Chips
            label="Account size"
            options={(plan?.sizes || []).map((s) => ({ value: s.label, label: s.label }))}
            value={size?.label || ''}
            onChange={setSizeLabel}
          />
          <Chips
            label="Time to pass"
            options={WINDOWS.map((w) => ({ value: w.days, label: w.label }))}
            value={windowDays}
            onChange={setWindowDays}
          />
        </div>

        {rules ? (
          <div className="pfp-rules-line">
            <div className="pfp-rules-chips" aria-label="Evaluation rules">
              {rulesSummary(rules).map((t) => <span key={t} className="pfp-rule">{t}</span>)}
              {edited ? <span className="pfp-rule is-edited">Edited</span> : null}
            </div>
            <div className="pfp-rules-actions">
              {edited ? <button type="button" className="pfp-link" onClick={() => applyRules(firmRules)}>Use the firm’s rules</button> : null}
              <button type="button" className="pfp-btn pfp-btn--ghost" onClick={() => setRulesOpen(true)}>Customize rules</button>
            </div>
          </div>
        ) : null}

        {firm ? (
          <p className={`pfp-verified${firm.confirmed === false ? ' is-unconfirmed' : ''}`}>
            {firm.confirmed === false
              ? `⚠ ${firm.firm}’s rules haven’t been confirmed against its own pages yet. RiskLo is checking them; treat these numbers as approximate and confirm before you buy.`
              : firm.status === 'changed' ? '⚠ The firm’s page may have changed; check before you buy.' : `✓ Rules checked ${formatVerified(firm.verifiedAt)} against ${firm.firm}’s own pages.`}
            {firm.source ? <> <a href={firm.source} target="_blank" rel="noreferrer">See the firm’s rules</a></> : null}
          </p>
        ) : null}
      </section>

      {error ? <div className="pfp-error" role="alert">{error}</div> : null}

      <section className={`pfp-card pfp-step${running && result ? ' is-updating' : ''}`} aria-label="Your best bet" aria-busy={running}>
        <StepTitle n={2} title="Your best bet">
          {running && result ? <span className="pfp-updating">Updating…</span> : null}
        </StepTitle>
        {running && !result ? <Skeleton hero /> : null}
        {result && result.strategyCount === 0 ? (
          <p className="pfp-muted">There are no futures strategies with results in this brand yet.</p>
        ) : null}
        {result && top ? (
          <div className={`pfp-hero pfp-top${top.warning ? ' is-flagged' : ''}`}>
            <PassRing rate={top.passRate} />
            <div className="pfp-hero-main">
              <div className="pfp-hero-title">
                <strong>{top.strategy} · {top.size}</strong>
                <RiskBadge rate={top.breachRate} />
              </div>
              <p className="pfp-hero-line">
                Started on any of <b>{top.runs.toLocaleString()} different dates</b>, it passed{' '}
                <b>{pct(top.passRate)}</b> of the time
                {top.medianDaysToPass != null ? <>, usually in <b>{timeToPass(top.medianDaysToPass)}</b></> : ''}.
                {' '}It failed <b>{pct(top.breachRate)}</b> of the time{top.timeoutRate ? <> and ran out of time {pct(top.timeoutRate)}</> : ''}.
              </p>
              <p className="pfp-hero-meta">{planLabel} · {windowLabel} to pass</p>
              {top.warning ? <p className="pfd-warn">{warningText(top.warning, result.rules)}</p> : null}
            </div>
            <div className="pfp-hero-actions">
              <button type="button" className="pfp-btn" onClick={seeTop} aria-expanded={openKey === rowKey(top)}>See how it went</button>
              <button type="button" className="pfp-btn pfp-btn--ghost" onClick={askAi}>
                <IconMessageSquare size={16} /> Ask RiskLo AI
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section ref={listRef} className={`pfp-card pfp-step${running && result ? ' is-updating' : ''}`} aria-label="Compare strategies">
        <StepTitle n={3} title="Compare strategies">
          <div className="pfp-view" role="tablist" aria-label="Show">
            <button type="button" role="tab" aria-selected={view === 'best'} className={view === 'best' ? 'active' : ''} onClick={() => setView('best')}>Top picks</button>
            <button type="button" role="tab" aria-selected={view === 'all'} className={view === 'all' ? 'active' : ''} onClick={() => setView('all')}>All sizes</button>
          </div>
        </StepTitle>
        <p className="pfp-muted pfp-hint">
          {view === 'best'
            ? 'Each strategy at its best size. Tap a strategy to see how it went.'
            : 'Every strategy at every size it can trade on this account. Tap one to see how it went.'}
        </p>

        {running && !result ? <Skeleton /> : null}
        {result && rows.length ? (
          <ResultsList
            rows={rows}
            rules={result.rules}
            openKey={openKey}
            onToggle={(r) => setOpenKey((k) => (k === rowKey(r) ? null : rowKey(r)))}
            renderDetail={(r) => (
              <PlannerRowDetail row={r} rules={result.rules} windowDays={result.windowDays} planLabel={planLabel} onAsk={askAboutRow} />
            )}
          />
        ) : null}
        {result && result.strategyCount > 0 && !rows.length && !locked ? (
          <p className="pfp-muted">No strategy has enough history to replay against this evaluation yet.</p>
        ) : null}

        {locked > 0 ? (
          <div className="pfp-locked">
            <IconLock size={16} />
            <span>{locked} more {locked === 1 ? 'row' : 'rows'} with Pro.</span>
            {onUpgrade ? <button type="button" className="pfp-btn" onClick={() => onUpgrade('Upgrade to Pro to see every strategy and size in the Prop Firm Planner.')}>Upgrade</button> : null}
          </div>
        ) : null}

        {result?.skipped?.length ? (
          <p className="pfp-muted">Not enough history yet: {result.skipped.map((s) => s.strategy).join(', ')}.</p>
        ) : null}

        {result ? (
          <details className="pfp-about">
            <summary>About these numbers</summary>
            <p>
              Only futures strategies are included (Stock / ETF, crypto and daily-balance strategies aren’t per-contract results).
              Each one’s daily results are replayed from every start date in its last three years. <b>Chance to pass</b> is how
              often it reached the target within the rules and the time allowed; <b>risk of failing</b> how often it hit the max
              loss. A strategy’s top pick is the size that passed most often, unless a size that passed nearly as often (within
              3 points) failed less; sizes too risky for the account aren’t picked.
            </p>
            <ul>{(result.caveats || []).map((c) => <li key={c}>{c}</li>)}</ul>
          </details>
        ) : null}
      </section>

      <RulesPanel open={rulesOpen} rules={rules} firmRules={firmRules} onApply={applyRules} onClose={() => setRulesOpen(false)} />
    </div>
  );
}

export { WINDOWS };
